import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import API from "../api";

// ADMIN → TRACKING — the owner pastes their Google IDs here once and the
// whole site (GA4 + Google Ads + Search Console verification) picks them up
// at runtime. No rebuild needed.

const FIELDS = [
  {
    key: "ga4_measurement_id",
    label: "GA4 Measurement ID",
    placeholder: "G-XXXXXXXXXX",
    hint: "Google Analytics → Admin → Data streams → Web → your stream. Starts with G-.",
    testid: "tracking-ga4-input",
  },
  {
    key: "ads_conversion_id",
    label: "Google Ads Conversion ID",
    placeholder: "AW-XXXXXXXXX",
    hint: "Google Ads → Goals → Conversions → Summary → your tag. Starts with AW-.",
    testid: "tracking-ads-input",
  },
  {
    key: "ads_label_enquiry",
    label: "Ads label — enquiry form (primary conversion)",
    placeholder: "Paste the conversion label for 'Enquiry submitted'",
    hint: "Google Ads → the conversion action's tag settings → Conversion label.",
    testid: "tracking-label-enquiry-input",
  },
  {
    key: "ads_label_phone",
    label: "Ads label — phone clicks (secondary conversion)",
    placeholder: "Paste the conversion label for 'Phone clicks'",
    hint: "Create a 'Phone clicks' conversion action, then copy its label.",
    testid: "tracking-label-phone-input",
  },
  {
    key: "ads_label_email",
    label: "Ads label — email clicks (secondary conversion)",
    placeholder: "Paste the conversion label for 'Email clicks'",
    hint: "Create an 'Email clicks' conversion action, then copy its label.",
    testid: "tracking-label-email-input",
  },
  {
    key: "gsc_verification_token",
    label: "Google Search Console verification code",
    placeholder: "The long code inside content=\"…\"",
    hint: "Search Console → verify via HTML tag → copy just the code between the quotes. (DNS verification at your registrar is even simpler — no code needed here.)",
    testid: "tracking-gsc-input",
  },
];

export default function TrackingManager() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/settings/tracking`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => setValues({ ...data }))
      .catch(() => setValues({}));
  }, []);

  if (!values) {
    return <p className="mt-10 text-center font-display text-2xl italic text-ivory/50">Loading tracking settings…</p>;
  }

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/settings/tracking`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.[0]?.msg || data.detail || "Save failed");
      toast.success("Tracking settings saved — live for every visitor straight away.");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-20">
      <p className="mt-6 max-w-3xl text-sm font-light leading-relaxed text-ivory/70">
        Paste your Google IDs below and save — Google Analytics 4, Google Ads conversions and
        Search Console verification all switch on immediately. Until then nothing is loaded and
        no visitor data is collected.
      </p>
      <div className="mt-6 grid max-w-3xl gap-5">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[11px] font-semibold uppercase tracking-widest2 text-gold">{f.label}</span>
            <input
              data-testid={f.testid}
              value={values[f.key] || ""}
              placeholder={f.placeholder}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              className="mt-2 w-full border border-ivory/25 bg-navy px-4 py-2.5 text-sm text-ivory placeholder:text-ivory/30 focus:border-gold focus:outline-none"
            />
            <span className="mt-1.5 block text-xs font-light text-ivory/50">{f.hint}</span>
          </label>
        ))}
        <div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="tracking-save-button"
            className="flex items-center gap-2 bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save tracking settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
